import React, { useEffect, useState, useRef } from "react";
import { theme } from "../theme.js";
import "leaflet/dist/leaflet.css";

export default function MapTrack({ trackData, title = "Track" }) {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!trackData) {
      console.log("[DEBUG] No trackData available");
      setLoading(false);
      return;
    }

    // Use requestAnimationFrame to ensure DOM is mounted
    let rafId = requestAnimationFrame(() => {
      setTimeout(async () => {
        if (!mapContainer.current) {
          console.log("[DEBUG] mapContainer.current is still null, retrying...");
          return;
        }

        console.log("[DEBUG] Container ready, loading map");

        try {
          const L = (await import("leaflet")).default;
        
        // Parse track data if needed
        let geoJsonData = trackData;
        if (typeof trackData === "string") {
          geoJsonData = JSON.parse(trackData);
        }

        console.log("[DEBUG] Parsed GeoJSON:", geoJsonData);

        if (!geoJsonData) {
          console.log("[DEBUG] No geoJsonData after parsing");
          setLoading(false);
          return;
        }

        // Handle both full geojson with geometry and direct geometry
        let geometry = geoJsonData.geometry || geoJsonData;
        
        if (!geometry || !geometry.coordinates || geometry.coordinates.length === 0) {
          console.log("[DEBUG] No valid coordinates found");
          setLoading(false);
          return;
        }

        // Initialize map only once
        if (!map.current) {
          console.log("[DEBUG] Creating new map");
          map.current = L.map(mapContainer.current, {
            attributionControl: true,
          });

          // Add tile layer
          L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
            maxZoom: 19,
          }).addTo(map.current);
        }

        // Create feature for GeoJSON
        const feature = {
          type: "Feature",
          geometry: geometry,
          properties: geoJsonData.properties || {}
        };

        console.log("[DEBUG] Feature created:", feature);

        // Add GeoJSON to map
        const geojsonLayer = L.geoJSON(feature, {
          style: {
            color: theme.colors.dark,
            weight: 3,
            opacity: 0.8,
            lineCap: "round",
            lineJoin: "round",
          },
          onEachFeature: (feature, layer) => {
            if (feature.properties) {
              const props = feature.properties;
              let popupContent = `<div style="font-size: 12px; font-family: Arial, sans-serif;">
                <strong>${props.name || title}</strong><br/>`;

              if (props.distance_m) {
                popupContent += `Distance: ${(props.distance_m / 1000).toFixed(2)} km<br/>`;
              }
              if (props.elevation_gain_m) {
                popupContent += `Dénivelé +: ${Math.round(props.elevation_gain_m)} m<br/>`;
              }
              if (props.elevation_loss_m) {
                popupContent += `Dénivelé -: ${Math.round(props.elevation_loss_m)} m<br/>`;
              }
              popupContent += "</div>";
              layer.bindPopup(popupContent);
            }
          },
        }).addTo(map.current);

        // Add start and finish markers
        const coords = geometry.coordinates;
        console.log("[DEBUG] Coordinates:", coords);
        if (coords.length > 0) {
          // Start marker (first point) - green
          const startCoord = coords[0];
          console.log("[DEBUG] Start coordinate:", startCoord, "as [lat, lng]:", [startCoord[1], startCoord[0]]);
          
          const startIcon = L.divIcon({
            html: `<div style="background-color: #22c55e; border: 2px solid #16a34a; width: 16px; height: 16px; border-radius: 50%; box-shadow: 0 0 6px rgba(0,0,0,0.4);"></div>`,
            iconSize: [16, 16],
            className: 'start-marker'
          });
          
          L.marker([startCoord[1], startCoord[0]], { icon: startIcon })
            .addTo(map.current)
            .bindPopup("<strong>Départ</strong>");

          // Finish marker (last point) - red
          const finishCoord = coords[coords.length - 1];
          console.log("[DEBUG] Finish coordinate:", finishCoord, "as [lat, lng]:", [finishCoord[1], finishCoord[0]]);
          
          const finishIcon = L.divIcon({
            html: `<div style="background-color: #ef4444; border: 2px solid #dc2626; width: 16px; height: 16px; border-radius: 50%; box-shadow: 0 0 6px rgba(0,0,0,0.4);"></div>`,
            iconSize: [16, 16],
            className: 'finish-marker'
          });
          
          L.marker([finishCoord[1], finishCoord[0]], { icon: finishIcon })
            .addTo(map.current)
            .bindPopup("<strong>Arrivée</strong>");
        }

        // Calculate bounds and fit
        if (coords.length > 0) {
          const lats = coords.map((c) => c[1]);
          const lons = coords.map((c) => c[0]);
          const bounds = L.latLngBounds(
            [Math.min(...lats), Math.min(...lons)],
            [Math.max(...lats), Math.max(...lons)]
          );
          console.log("[DEBUG] Bounds calculated, fitting map");
          map.current.fitBounds(bounds, { padding: [50, 50] });
        }

        setLoading(false);
        console.log("[DEBUG] Map loaded successfully");
      } catch (error) {
        console.error("Error loading map:", error);
        setLoading(false);
      }
      }, 200);
    });

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [trackData, title]);

  if (!trackData) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h3 style={styles.title}>{title}</h3>
        </div>
        <div style={styles.mapContainer}>
          <div style={styles.errorMessage}>Aucune donnée de parcours disponible</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h3 style={styles.title}>{title}</h3>
      </div>
      <div ref={mapContainer} style={styles.mapContainer}>
        {loading && <div style={styles.loadingMessage}>Chargement de la carte...</div>}
      </div>
    </div>
  );
}

const styles = {
  container: {
    width: "100%",
    borderRadius: theme.radius.lg,
    overflow: "hidden",
    border: `1px solid ${theme.colors.border}`,
    backgroundColor: theme.colors.white,
    marginBottom: theme.spacing.lg,
    zIndex: 0,
  },
  header: {
    padding: theme.spacing.md,
    borderBottom: `1px solid ${theme.colors.border}`,
    backgroundColor: "#f9fafb",
  },
  title: {
    fontSize: theme.fonts.size.lg,
    fontWeight: theme.fonts.weight.semibold,
    color: theme.colors.textInverted,
    margin: 0,
  },
  mapContainer: {
    width: "100%",
    height: "400px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  loadingMessage: {
    padding: theme.spacing.lg,
    textAlign: "center",
    color: theme.colors.textMuted,
    fontSize: theme.fonts.size.sm,
  },
  errorMessage: {
    padding: theme.spacing.lg,
    textAlign: "center",
    color: theme.colors.error,
    fontSize: theme.fonts.size.sm,
  },
};
